import dayjs from 'dayjs'
import { atom, useAtom } from 'jotai'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'

import privateSessionStateAtom from '../../contexts/AtomPrivateSessions'
import { usePrivateSession } from '../../contexts/PrivateSessionContext'
import { isInstructorAtom } from '../../domains/auth/AuthSelectors'
import { currentUserAtom } from '../../domains/user/AtomUsers'
import PrivateSessionService from '../../services/PrivateSessionService'
import { STATUS } from '../../share/Constant'
import { DEFAULT_FORM_VALUES } from '../../share/Constant'
import { formatTimeSlot, formatToDate, isOverlappingSession, isSameSession } from '../../utils/TimeUtility'
import useBar from '../common/Bar'

const timeSlotToSession = (formData, instructorId, date, timeSlot, sessionId = null) => {
  const startTime = timeSlot.start
  const endTime = timeSlot.end
  const registrationDeadlineTime = startTime.subtract(formData.registrationDeadline, 'hour')
  const cancellationDeadlineTime = startTime.subtract(formData.cancellationDeadline, 'hour')

  const session = {
    skiResort: formData.skiResort,
    sport: formData.sport,
    date: date,
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
    instructorId: instructorId,
    price: Number(formData.price),
    capacity: Number(formData.capacity),
    description: formData.description,
    registrationDeadline: registrationDeadlineTime.toISOString(),
    cancellationDeadline: cancellationDeadlineTime.toISOString(),
  }

  if (sessionId) {
    session.resourceId = sessionId
  }

  return session
}

const sessionToTimeSlot = (session) => ({
  value: formatTimeSlot(session.startTime, session.endTime),
  label: formatTimeSlot(session.startTime, session.endTime),
  start: dayjs(session.startTime),
  end: dayjs(session.endTime),
  status: session.status,
})

/**
 * Generates an array of time slots for a given date
 * Each slot is 90 minutes long between 8:00-21:00
 *
 * @param {string} date - The date to generate slots for in YYYY-MM-DD format
 * @returns {Array} Array of time slot objects with:
 *   - start: Dayjs object for slot start time
 *   - end: Dayjs object for slot end time
 *   - label: Formatted time range string (HH:mm-HH:mm)
 *   - value: Same as label, used as unique identifier
 *   - status: Default STATUS.DISABLE
 */
const timeSlotTemplate = (date, { halfhour = false, startHour = 8, endHour = 21, sessionDuration = 60 } = {}) => {
  const sessionGap = 60

  const slots = []
  let currentTime = dayjs(date)
    .set('hour', startHour)
    .set('minute', halfhour ? 30 : 0)
    .set('second', 0)
    .set('millisecond', 0)

  let now = dayjs()

  const endTime = dayjs(date).set('hour', endHour).set('minute', 0).set('second', 0).set('millisecond', 0)

  while (currentTime.isBefore(endTime)) {
    if (currentTime.isBefore(now)) {
      currentTime = currentTime.add(sessionGap, 'minute')
      continue
    }

    const endTimeSlot = currentTime.add(sessionDuration, 'minute')
    slots.push({
      start: currentTime,
      end: endTimeSlot,
      label: formatTimeSlot(currentTime, endTimeSlot),
      value: formatTimeSlot(currentTime, endTimeSlot),
      status: STATUS.DISABLE,
    })
    currentTime = currentTime.add(sessionGap, 'minute')
  }

  return slots
}

const recalculateTimeSlotStatus = (timeSlots) => {
  timeSlots.map((timeSlot) => {
    if (timeSlot.status === STATUS.LOCKED) {
      timeSlot.status = STATUS.DISABLE
    }
  })

  for (const timeSlotA of timeSlots) {
    if (timeSlotA.status !== STATUS.DISABLE) continue // Only need to update disable time slots

    for (const timeSlotB of timeSlots) {
      if (timeSlotB?.status === STATUS.DISABLE || timeSlotB?.status === STATUS.LOCKED) continue // Only need to check against non-disable time slots

      // loop against existing time slots
      if (isSameSession({ startTime: timeSlotB.start, endTime: timeSlotB.end }, { startTime: timeSlotA.start, endTime: timeSlotA.end })) {
        continue
      }

      if (isOverlappingSession({ startTime: timeSlotB.start, endTime: timeSlotB.end }, { startTime: timeSlotA.start, endTime: timeSlotA.end })) {
        timeSlotA.status = STATUS.LOCKED
        break
      }
    }
  }
}

const calculateChangedSessions = (formData, instructorId, myInstructorSessions, date, timeSlots) => {
  const findSession = (timeSlot) => {
    return myInstructorSessions
      .filter((session) => formatToDate(session.date) === date)
      .find((session) => isSameSession({ startTime: session.startTime, endTime: session.endTime }, { startTime: timeSlot.start, endTime: timeSlot.end }))
  }

  const isSessionCreated = (timeSlot) => {
    const session = findSession(timeSlot)
    return session !== null && session !== undefined
  }

  // Combine available time slots with instructor sessions, removing duplicates
  const openTimeSlots = [...timeSlots.filter((timeSlot) => timeSlot.status === STATUS.AVAILABLE || timeSlot.status === STATUS.BOOKED)]

  const sessionsToDelete = timeSlots
    .filter((timeSlot) => timeSlot.status === STATUS.DISABLE || timeSlot.status === STATUS.LOCKED)
    .filter(isSessionCreated)
    .map(findSession)

  const sessionsToCreate = timeSlots
    .filter((timeSlot) => timeSlot.status === STATUS.AVAILABLE)
    .filter((timeSlot) => !findSession(timeSlot))
    .map((timeSlot) => timeSlotToSession(formData, instructorId, date, timeSlot))

  const sessionsToUpdate = timeSlots
    .filter((timeSlot) => timeSlot.status === STATUS.AVAILABLE)
    .map((timeSlot) => {
      const session = findSession(timeSlot)
      if (session === null || session === undefined) return null

      const newSession = timeSlotToSession(formData, instructorId, date, timeSlot, session.resourceId)
      const needsUpdate =
        session.sport !== formData.sport ||
        session.skiResort !== formData.skiResort ||
        session.price !== Number(formData.price) ||
        session.description !== formData.description ||
        session.registrationDeadline !== newSession.registrationDeadline ||
        session.cancellationDeadline !== newSession.cancellationDeadline

      if (needsUpdate) {
        return timeSlotToSession(formData, instructorId, date, timeSlot, session.resourceId)
      }

      return null
    })
    .filter((session) => session !== null && session !== undefined)

  return { sessionsToCreate, sessionsToUpdate, sessionsToDelete, openTimeSlots }
}

const calculateTimeSlots = (date, allTimeSlots, myInstructorSessions) => {
  const openSessionsOnDate = myInstructorSessions.filter((session) => formatToDate(session.startTime) === date).filter((session) => dayjs(session.startTime).isAfter(dayjs()))

  // Create time slots for the selected date
  const timeSlots = allTimeSlots?.map((timeSlotTemplate) => {
    const timeSlot = { ...timeSlotTemplate }
    timeSlot.key = `${date}-${timeSlot.value}`

    return timeSlot
  })

  // Add open sessions to time slots
  openSessionsOnDate.map(sessionToTimeSlot).forEach((timeSlot) => {
    const matchedTimeSlot = timeSlots.find((ts) => isSameSession({ startTime: ts.start, endTime: ts.end }, { startTime: timeSlot.start, endTime: timeSlot.end }))

    if (matchedTimeSlot) {
      matchedTimeSlot.status = timeSlot.status
    } else {
      timeSlots.push(timeSlot)
    }
  })

  // Sort time slots by start time
  timeSlots.sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))

  // Recalculate time slot status
  recalculateTimeSlotStatus(timeSlots)
  return timeSlots
}

const toggleTimeSlotAtom = atom(null, (get, set, timeSlot) => {
  set(privateSessionStateAtom.timeSlots, (prev) => {
    const newTimeSlots = prev.map((t) => (t.value === timeSlot.value ? { ...t, status: t.status === STATUS.AVAILABLE ? STATUS.DISABLE : STATUS.AVAILABLE } : t))
    recalculateTimeSlotStatus(newTimeSlots)
    return newTimeSlots
  })
})

/**
 * Custom hook for managing private session creation
 * Handles form state, validation, and submission
 * Separates business logic from UI
 */
const usePrivateSessionCreation = () => {
  const navigate = useNavigate()
  const user = useAtomValue(currentUserAtom)
  const isInstructor = useAtomValue(isInstructorAtom)

  const bar = useBar()
  const { myInstructorSessions, fetchMyInstructorSessions, isLoading } = usePrivateSession()

  // Form state
  const [date] = useAtom(privateSessionStateAtom.date)
  const [, setSessionsChanges] = useAtom(privateSessionStateAtom.sessionsChanges)
  const [formData, setFormData] = useAtom(privateSessionStateAtom.form)
  const [, setFormErrors] = useAtom(privateSessionStateAtom.formError)
  const [timeSlotOptions] = useAtom(privateSessionStateAtom.timeSlotOptions)
  const [timeSlots, setTimeSlots] = useAtom(privateSessionStateAtom.timeSlots)
  const [template, setTemplate] = useAtom(privateSessionStateAtom.template)

  // Generate all possible time slots
  const allTimeSlots = useMemo(() => timeSlotTemplate(date, timeSlotOptions), [date, timeSlotOptions])

  // Check if user is instructor and load instructor sessions
  useEffect(() => {
    if (!isInstructor) {
      navigate('/')
      return
    }

    fetchMyInstructorSessions()
  }, [user, isInstructor, fetchMyInstructorSessions])

  // Calculate time slots
  useEffect(() => {
    setTimeSlots(calculateTimeSlots(date, allTimeSlots, myInstructorSessions))
  }, [date, allTimeSlots, myInstructorSessions, setTimeSlots])

  // Set form data based on instructor sessions
  // Also change to a better default based on myInstructorSessions
  useEffect(() => {
    const openSessionsOnDate = myInstructorSessions.filter((session) => formatToDate(session.startTime) === date).filter((session) => dayjs(session.startTime).isAfter(dayjs()))

    // Change Ski Resort based on selected date
    if (openSessionsOnDate.length !== 0) {
      const session = openSessionsOnDate[0]

      const resort = session.skiResort
      const templateData = template[resort] || {}
      const registrationDeadline = templateData?.registrationDeadline || DEFAULT_FORM_VALUES.registrationDeadline
      const cancellationDeadline = templateData?.cancellationDeadline || DEFAULT_FORM_VALUES.cancellationDeadline

      const privateFormInfo = {
        ...templateData,
        skiResort: resort,
        sport: session.sport,
        price: Number(session.price),
        description: session.description,
        registrationDeadline: registrationDeadline,
        cancellationDeadline: cancellationDeadline,

        // TODO THIS IS NOT WORKING YET, using from template instead of session
        // registrationDeadline: Number(session.registrationDeadline),
        // cancellationDeadline: Number(session.cancellationDeadline),
      }

      setFormData(privateFormInfo)
    }
  }, [date, myInstructorSessions, setFormData, template])

  const [, toggleTimeSlot] = useAtom(toggleTimeSlotAtom)

  // Validate form data
  const validateForm = useCallback(() => {
    const errors = {}
    if (!formData.skiResort) errors.skiResort = 'Please select a location'
    if (!formData.sport) errors.sport = 'Please select a sport type'
    // if (!formData.timeSlots.length) errors.timeSlots = 'Please select at least one time slot'
    if (!formData.price) errors.price = 'Please enter a price'
    if (formData.price < 0) errors.price = 'Price must be greater than 0'
    if (formData.registrationDeadline < 0) errors.registrationDeadline = 'Registration deadline must be positive'
    if (formData.cancellationDeadline < 0) errors.cancellationDeadline = 'Cancellation deadline must be positive'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
    }

    if (Object.keys(errors).length > 0) {
      bar.error(Object.values(errors)[0])
      return false
    }
    return true
  }, [formData, bar])

  // Handle form submission
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      if (!validateForm()) return false

      const { sessionsToCreate, sessionsToUpdate, sessionsToDelete, openTimeSlots } = calculateChangedSessions(formData, user.resourceId, myInstructorSessions, date, timeSlots)

      const isChanged = sessionsToCreate.length > 0 || sessionsToUpdate.length > 0 || sessionsToDelete.length > 0

      setSessionsChanges({
        isChanged: isChanged,
        sessionsToCreate,
        sessionsToUpdate,
        sessionsToDelete,
        openTimeSlots,
      })
      return true
    },
    [validateForm, setSessionsChanges, formData, user?.resourceId, myInstructorSessions, date, timeSlots],
  )

  return {
    isLoading,
    handleSubmit,
    toggleTimeSlot,
  }
}

const usePrivateSessionCreationHandleConfirmSubmit = () => {
  const bar = useBar()
  const { fetchMyInstructorSessions } = usePrivateSession()

  // Form state
  const [, setIsSubmitting] = useAtom(privateSessionStateAtom.submit)
  const [sessionsChanges, setSessionsChanges] = useAtom(privateSessionStateAtom.sessionsChanges)
  const [formData] = useAtom(privateSessionStateAtom.form)
  const [template, setTemplate] = useAtom(privateSessionStateAtom.template)

  const resetSessionsChanges = useCallback(() => {
    setSessionsChanges({ isChanged: false, sessionsToCreate: [], sessionsToUpdate: [], sessionsToDelete: [], openTimeSlots: [] })
  }, [])

  // Handle confirmation submission
  const handleConfirmSubmit = useCallback(async () => {
    if (!sessionsChanges.isChanged) return

    setIsSubmitting(true)

    try {
      await Promise.all([
        ...sessionsChanges.sessionsToDelete.map((session) => PrivateSessionService.deletePrivateSession(session.resourceId)),
        ...sessionsChanges.sessionsToUpdate.map((session) => PrivateSessionService.updatePrivateSession(session.resourceId, session)),
        ...sessionsChanges.sessionsToCreate.map((session) => PrivateSessionService.createPrivateSession(session)),
      ])

      resetSessionsChanges()
      bar.success(`Your changes have been saved`)

      await fetchMyInstructorSessions()
      setTemplate((prev) => ({ ...prev, [formData.skiResort]: formData }))

      return true // Return success status
    } catch (error) {
      bar.error(error.message || 'Failed to save your changes. Please try again.')
      return false // Return failure status
    } finally {
      setIsSubmitting(false)
    }
  }, [sessionsChanges, fetchMyInstructorSessions, bar, resetSessionsChanges])

  return {
    handleConfirmSubmit,
  }
}

export { usePrivateSessionCreation, usePrivateSessionCreationHandleConfirmSubmit }
